import React, { useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HERO_BG = "https://images.unsplash.com/photo-1759971646397-2978128581d7?auto=format&fit=crop&w=1600&q=80";

export default function AISageTip({ scope = "daily", location, plantId = null, title }) {
  const [guidance, setGuidance] = useState("");
  const [loading, setLoading] = useState(false);

  const summon = async () => {
    setLoading(true);
    try {
      const r = await api.post("/sage", {
        scope,
        plant_id: plantId,
        latitude: location?.latitude,
        longitude: location?.longitude,
        location_label: location?.label,
      });
      setGuidance(r.data.guidance);
    } catch (e) {
      toast.error("The Sage is silent. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative rounded-lg overflow-hidden border border-white/10 grain-overlay"
      style={{ minHeight: 320 }}
      data-testid={`sage-tip-${scope}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f1d18]/85 via-[#1a2b22]/75 to-[#0f1d18]/85" />
      {/* twinkling stars */}
      {[...Array(7)].map((_, i) => (
        <span
          key={i}
          className="absolute w-1 h-1 rounded-full bg-accent star-twinkle"
          style={{
            top: `${(i * 17 + 8) % 95}%`,
            left: `${(i * 23 + 11) % 95}%`,
            animationDelay: `${i * 0.4}s`,
          }}
        />
      ))}

      <div className="relative p-8 lg:p-10 text-white">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-overline text-accent/80">{title || "Whisper from the Sage"}</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-1 font-light tracking-tight">
              {scope === "plant" ? "Counsel for this plant" :
                scope === "weekly" ? "This week, in the garden" :
                scope === "monthly" ? "The month ahead" : "Today's quiet guidance"}
            </h2>
          </div>
          <Button
            onClick={summon}
            disabled={loading || !location}
            className="bg-accent text-accent-foreground hover:bg-accent/90 gap-2 glow-amber"
            data-testid={`summon-sage-${scope}`}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {guidance ? "Re-summon" : "Summon the Sage"}
          </Button>
        </div>

        {!location && (
          <div className="crystal-glass rounded-md p-5 text-sm text-foreground/90">
            Set your location first — the Sage reads the local weather to give true counsel.
          </div>
        )}

        {location && !guidance && !loading && (
          <div className="crystal-glass rounded-md p-5 text-sm text-foreground/90">
            Press <em>Summon</em> and the Sage will weave today's weather, your plants, and the
            season into focused guidance.
          </div>
        )}

        {loading && (
          <div className="crystal-glass rounded-md p-5 text-sm text-foreground/90 fade-up">
            Drawing counsel from the moon and rain…
          </div>
        )}

        {guidance && !loading && (
          <div
            className="crystal-glass rounded-md p-6 text-foreground/95 leading-relaxed whitespace-pre-line fade-up"
            data-testid={`sage-guidance-${scope}`}
          >
            {guidance}
          </div>
        )}
      </div>
    </div>
  );
}
